import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// Firebase
import { db } from "../../../../firebaseConfig";

// global
import { useAuth } from '../../../../AuthContext';

// redux
import { useAppSelector } from '../../../../redux/store';

// function fetchCallData
import { fetchCallMetrics } from '../../../../components/CallCRM/Function';
import getCurrentScreen from '../../../../Helper/GetScreen';

// Call Details Panel
import CallDetailsPanel from '../../../../components/CallCRM/CallDetailsPanel';
import { CallLogFilters } from '../../../../components/CallCRM/DetailedCallLogService';

interface UserOption {
  id: string;
  name: string;
}

interface CallDistributionProps {
  users: UserOption[];
  selectedUser: UserOption[];
  startTime: string | null;
  endTime: string | null;
}

interface CallMetrics {
  outboundCalls: number;
  missedCalls: number;
  connected: number;
  inboundCalls: number;
  outboundDuration: number;
  inboundDuration: number;
  totalDuration: number;
  durationZero: number;
  durationNotZero: number;
  callAttemptTarget: number;
}

const OutboundCalls: React.FC<CallDistributionProps> = ({ users, selectedUser, startTime, endTime }) => {
  const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
  
  // data state
  const [callMetrics, setCallMetrics] = React.useState<CallMetrics>();
  
  // Panel state
  const [isPanelOpen, setIsPanelOpen] = React.useState(false);
  const [selectedCallType, setSelectedCallType] = React.useState<'ATTEMPTED' | 'CONNECTED' | 'RECEIVED' | 'MISSED'>('ATTEMPTED');

  // Function to check if it's a single day selection
  const isSingleDay = () => {
    if (!startTime || !endTime) return false;
    
    // Extract date part only (YYYY-MM-DD) to compare dates without time
    const startDate = startTime.split('T')[0];
    const endDate = endTime.split('T')[0];
    
    return startDate === endDate;
  };

  // Function to check if target should be shown
  const shouldShowTarget = () => {
    return isSingleDay();
  };

  React.useEffect(() => {
    fetchCallMetrics({ db, userData, startTime: startTime || null, endTime: endTime || null, users, currentScreen, selectedUser })
      .then((data) => { setCallMetrics(data); })
      .catch((error) => console.error("Error fetching call logs:", error));
  }, [userData?.companyId, startTime, endTime, users, currentScreen, selectedUser]);

  // Handle bar click
  const handleBarClick = (data: any) => {
    if (!userData?.companyId) return;
    
    let callType: 'ATTEMPTED' | 'CONNECTED' | 'RECEIVED' | 'MISSED';
    switch (data.name) {
      case 'Attempted':
        callType = 'ATTEMPTED';
        break;
      case 'Outbound Connected':
        callType = 'CONNECTED';
        break;
      case 'Received':
        callType = 'RECEIVED';
        break;
      case 'Missed':
        callType = 'MISSED';
        break;
      default:
        return;
    }
    
    setSelectedCallType(callType);
    setIsPanelOpen(true);
  };

  // Close panel
  const handleClosePanel = () => {
    setIsPanelOpen(false);
  };

  // Prepare filters for the panel
  const getPanelFilters = (): Omit<CallLogFilters, 'callType'> => {
    // If no specific users are selected, pass undefined to show ALL staff data
    // If specific users are selected, pass their IDs
    const userIds = selectedUser && selectedUser.length > 0 
      ? selectedUser.map(user => user.id)
      : undefined; // Don't pass userIds to show all staff

    return {
      companyId: userData?.companyId || '',
      startTime,
      endTime,
      userIds
    };
  };

  // Prepare chart data
  const chartData = callMetrics ? [
    {
      name: 'Attempted',
      value: callMetrics.outboundCalls || 0,
    //   target: shouldShowTarget() ? callMetrics.callAttemptTarget || 0 : null,
      color: '#02385e' // Updated color
    },
    {
      name: 'Outbound Connected',
      value: callMetrics.connected || 0,
      color: '#3483be' // cyan-600
    },
    {
      name: 'Received',
      value: callMetrics.inboundCalls || 0,
      color: '#a6c7e6' // sky-950
    },
    {
      name: 'Missed',
      value: callMetrics.missedCalls || 0,
      color: '#ef4444' // red-500
    }
  ] : [];

  // Custom tooltip to show target for attempted calls
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{`${label}: ${payload[0].value}`}</p>
          {data.target && shouldShowTarget() && (
            <p className="text-sm text-gray-600">{`Target: ${data.target}`}</p>
          )}
          <p className="text-xs text-blue-600 mt-1">Click to view details</p>
        </div>
      );
    }
    return null;
  };

  // Custom label function to show target for attempted calls
  const CustomLabel = (props: any) => {
    const { x, y, width, height, payload, value } = props;
    
    // Safety check for payload
    if (!payload) return null;
    
    if (payload.name === 'Attempted' && shouldShowTarget() && payload.target) {
      return (
        <text 
          x={x + width / 2} 
          y={y - 5} 
          textAnchor="middle" 
          fontSize="12" 
          fill="#666"
        >
          {`${value}/${payload.target}`}
        </text>
      );
    }
    return (
      <text 
        x={x + width / 2} 
        y={y - 5} 
        textAnchor="middle" 
        fontSize="12" 
        fill="#666"
      >
        {value}
      </text>
    );
  };

  return (
    <div className="bg-white space-y-4 w-full overflow-hidden">
      {/* Chart Component */}
      <div className="border border-cyan-600 rounded-lg p-4 overflow-hidden">
        <h2
          className="text-xl text-black font-medium text-center mb-4"
          style={{
            fontFamily: "Montserrat",
            lineHeight: "29.26px",
          }}
        >
          Call Metrics Overview
        </h2>
        
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{
              top: 30,
              right: 20,
              left: 20,
              bottom: 5,
            }}
            onMouseEnter={() => {
              // Add hover effect by changing cursor
              document.body.style.cursor = 'pointer';
            }}
            onMouseLeave={() => {
              document.body.style.cursor = 'default';
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="name" 
              tick={false}
              axisLine={{ stroke: '#e5e7eb' }}
              interval={0}
            />
            <YAxis 
              tick={{ fontSize: 11 }}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="value" 
              radius={[4, 4, 0, 0]}
              label={<CustomLabel />}
              onClick={handleBarClick}
              style={{ cursor: 'pointer' }}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        
        {/* Legend */}
        <div className="flex flex-wrap justify-center mt-4 gap-3 sm:gap-6 text-sm max-w-full">
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-75 transition-all duration-200 hover:scale-105 flex-shrink-0"
            onClick={() => handleBarClick({ name: 'Attempted' })}
            title={`Click to view ${callMetrics?.outboundCalls || 0} attempted calls`}
          >
            <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: '#02385e' }}></div>
            <span className="whitespace-nowrap">Attempted ({callMetrics?.outboundCalls || 0})</span>
          </div>
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-75 transition-all duration-200 hover:scale-105 flex-shrink-0"
            onClick={() => handleBarClick({ name: 'Connected' })}
            title={`Click to view ${callMetrics?.connected || 0} Outbound Connected calls`}
          >
            <div className="w-3 h-3 bg-cyan-600 rounded flex-shrink-0"></div>
            <div className="text-center leading-tight">
              <div>Outbound</div>
              <div>Connected ({callMetrics?.connected || 0})</div>
            </div>
          </div>
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-75 transition-all duration-200 hover:scale-105 flex-shrink-0"
            onClick={() => handleBarClick({ name: 'Received' })}
            title={`Click to view ${callMetrics?.inboundCalls || 0} received calls`}
          >
            <div className="w-3 h-3 bg-sky-950 rounded flex-shrink-0"></div>
            <span className="whitespace-nowrap">Received ({callMetrics?.inboundCalls || 0})</span>
          </div>
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-75 transition-all duration-200 hover:scale-105 flex-shrink-0"
            onClick={() => handleBarClick({ name: 'Missed' })}
            title={`Click to view ${callMetrics?.missedCalls || 0} missed calls`}
          >
            <div className="w-3 h-3 bg-red-500 rounded flex-shrink-0"></div>
            <span className="whitespace-nowrap">Missed ({callMetrics?.missedCalls || 0})</span>
          </div>
        </div>
      </div>

      {/* Call Details Panel */}
      <CallDetailsPanel
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        callType={selectedCallType}
        filters={getPanelFilters()}
      />
    </div>
  );
};

export default OutboundCalls;